import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Person as PersonIcon, Save as SaveIcon } from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import {
  clearCurrentMedicalTransportOrder,
  createMedicalTransportOrder,
  fetchMedicalTransportOrderById,
  updateMedicalTransportOrder
} from '../../redux/slices/medicalTransportOrdersSlice';
import { fetchCustomers } from '../../redux/slices/customersSlice';
import { SPECIAL_CAPABILITIES, VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';
import {
  APPROVAL_STATUS_OPTIONS,
  BILLING_STATUS_OPTIONS,
  COPAYMENT_STATUS_OPTIONS,
  DOCUMENT_STATUS_OPTIONS,
  ROUTE_SCOPE_OPTIONS,
  TREATMENT_TYPE_OPTIONS,
  toDateInputValue
} from './medicalTransportOrderOptions';

const emptyForm = {
  customerId: '',
  patientName: '',
  patientPhone: '',
  healthInsuranceName: '',
  insuranceNumber: '',
  prescriptionNumber: '',
  approvalNumber: '',
  approvalRequired: true,
  approvalStatus: 'missing',
  approvalRequestedAt: '',
  approvalApprovedAt: '',
  approvalValidUntil: '',
  validFrom: '',
  validUntil: '',
  maxRides: '',
  usedRides: 0,
  plannedRides: 0,
  routeScope: 'fixed_route',
  pickupAddress: '',
  pickupLatitude: '',
  pickupLongitude: '',
  destinationAddress: '',
  destinationLatitude: '',
  destinationLongitude: '',
  treatmentType: 'other',
  transportRequirementMode: 'vehicle_type',
  requiredCapabilities: [],
  defaultVehicleType: 1,
  defaultNotes: '',
  escortRequired: false,
  copaymentStatus: 'unknown',
  copaymentAmountEur: '',
  documentStatus: 'incomplete',
  billingStatus: 'not_ready'
};

const optionItems = (options) => options.map(option => (
  <MenuItem key={option.value} value={option.value}>
    {option.label}
  </MenuItem>
));

const MedicalTransportOrderForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOrder, isLoading, isSaving, error } = useSelector(state => state.medicalTransportOrders);
  const { customers, isLoading: customersLoading } = useSelector(state => state.customers);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    dispatch(fetchCustomers());
    if (isEditMode) {
      dispatch(fetchMedicalTransportOrderById(id));
    } else {
      dispatch(clearCurrentMedicalTransportOrder());
      setForm(emptyForm);
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !currentOrder) return;
    setForm({
      customerId: currentOrder.customerId || '',
      patientName: currentOrder.patientName || '',
      patientPhone: currentOrder.patientPhone || '',
      healthInsuranceName: currentOrder.healthInsuranceName || '',
      insuranceNumber: currentOrder.insuranceNumber || '',
      prescriptionNumber: currentOrder.prescriptionNumber || '',
      approvalNumber: currentOrder.approvalNumber || '',
      approvalRequired: currentOrder.approvalRequired !== false,
      approvalStatus: currentOrder.approvalStatus || 'missing',
      approvalRequestedAt: toDateInputValue(currentOrder.approvalRequestedAt),
      approvalApprovedAt: toDateInputValue(currentOrder.approvalApprovedAt),
      approvalValidUntil: toDateInputValue(currentOrder.approvalValidUntil),
      validFrom: toDateInputValue(currentOrder.validFrom),
      validUntil: toDateInputValue(currentOrder.validUntil),
      maxRides: currentOrder.maxRides || '',
      usedRides: currentOrder.usedRides || 0,
      plannedRides: currentOrder.plannedRides || 0,
      routeScope: currentOrder.routeScope || 'fixed_route',
      pickupAddress: currentOrder.pickupAddress || '',
      pickupLatitude: currentOrder.pickupLatitude || '',
      pickupLongitude: currentOrder.pickupLongitude || '',
      destinationAddress: currentOrder.destinationAddress || '',
      destinationLatitude: currentOrder.destinationLatitude || '',
      destinationLongitude: currentOrder.destinationLongitude || '',
      treatmentType: currentOrder.treatmentType || 'other',
      transportRequirementMode: currentOrder.transportRequirementMode || (currentOrder.requiredCapabilities?.length ? 'capability' : 'vehicle_type'),
      requiredCapabilities: currentOrder.requiredCapabilities || [],
      defaultVehicleType: currentOrder.defaultVehicleType || 1,
      defaultNotes: currentOrder.defaultNotes || '',
      escortRequired: currentOrder.escortRequired === true,
      copaymentStatus: currentOrder.copaymentStatus || 'unknown',
      copaymentAmountEur: currentOrder.copaymentAmountEur || '',
      documentStatus: currentOrder.documentStatus || 'incomplete',
      billingStatus: currentOrder.billingStatus || 'not_ready'
    });
  }, [currentOrder, isEditMode]);

  const capabilityOptions = useMemo(
    () => Object.entries(SPECIAL_CAPABILITIES).map(([value, label]) => ({ value, label })),
    []
  );

  const selectedCustomer = useMemo(
    () => customers.find(customer => customer.id === form.customerId) || null,
    [customers, form.customerId]
  );

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (event, customer) => {
    setForm(prev => ({
      ...prev,
      customerId: customer?.id || '',
      patientName: customer ? (customer.name || customer.user?.name || prev.patientName) : prev.patientName,
      patientPhone: customer ? (customer.phoneNumber || customer.phone || customer.user?.phoneNumber || prev.patientPhone) : prev.patientPhone
    }));
  };

  const handleTransportRequirementModeChange = (event) => {
    const mode = event.target.value;
    setForm(prev => ({
      ...prev,
      transportRequirementMode: mode,
      defaultVehicleType: mode === 'vehicle_type' ? (prev.defaultVehicleType || 1) : '',
      requiredCapabilities: mode === 'capability' ? prev.requiredCapabilities : []
    }));
  };

  const handlePickupSelect = (address, location) => {
    setForm(prev => ({
      ...prev,
      pickupAddress: address,
      pickupLatitude: location?.latitude || '',
      pickupLongitude: location?.longitude || ''
    }));
  };

  const handleDestinationSelect = (address, location) => {
    setForm(prev => ({
      ...prev,
      destinationAddress: address,
      destinationLatitude: location?.latitude || '',
      destinationLongitude: location?.longitude || ''
    }));
  };

  const validate = () => {
    const errors = {};
    if (!form.patientName.trim()) errors.patientName = 'Patient ist erforderlich';
    if (!form.healthInsuranceName.trim()) errors.healthInsuranceName = 'Krankenkasse ist erforderlich';
    if (!form.validFrom) errors.validFrom = 'Gültig ab ist erforderlich';
    if (!form.validUntil) errors.validUntil = 'Gültig bis ist erforderlich';
    if (form.validFrom && form.validUntil && new Date(form.validFrom) > new Date(form.validUntil)) {
      errors.validUntil = 'Gültig bis muss nach Gültig ab liegen';
    }
    if (!form.pickupAddress.trim()) errors.pickupAddress = 'Startadresse ist erforderlich';
    if (!form.destinationAddress.trim()) errors.destinationAddress = 'Ziel/Einrichtung ist erforderlich';
    if (!Number(form.maxRides) || Number(form.maxRides) < 1) {
      errors.maxRides = 'Mindestens eine Fahrt ist erforderlich';
    }
    if (form.transportRequirementMode === 'vehicle_type' && !form.defaultVehicleType) {
      errors.defaultVehicleType = 'Bitte Fahrzeugtyp auswählen';
    }
    if (form.transportRequirementMode === 'capability' && form.requiredCapabilities.length === 0) {
      errors.requiredCapabilities = 'Bitte mindestens eine Spezialfähigkeit auswählen';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const action = isEditMode
      ? updateMedicalTransportOrder({ id, ...form })
      : createMedicalTransportOrder(form);
    const result = await dispatch(action);
    if (!result.error) {
      navigate(`/krankenfahrten/${result.payload.id}`);
    }
  };

  if (isEditMode && isLoading && !currentOrder) {
    return <Loading message="Verordnung wird geladen..." />;
  }

  if (isEditMode && error && !currentOrder) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Verordnung"
        message={error}
        onRetry={() => dispatch(fetchMedicalTransportOrderById(id))}
      />
    );
  }

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Verordnung bearbeiten' : 'Verordnung anlegen'}
        description="Verordnungsfall für wiederkehrende Krankenfahrten erfassen"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Krankenfahrten', path: '/krankenfahrten' },
          { label: isEditMode ? 'Bearbeiten' : 'Neu', path: isEditMode ? `/krankenfahrten/${id}/bearbeiten` : '/krankenfahrten/neu' }
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Patient und Krankenkasse</Typography>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Autocomplete
                      options={customers}
                      loading={customersLoading}
                      value={selectedCustomer}
                      onChange={handleCustomerChange}
                      getOptionLabel={(option) => option?.name || option?.user?.name || option?.email || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      filterOptions={(options, params) => {
                        const term = params.inputValue.toLowerCase();
                        if (!term) return options;
                        return options.filter((option) => {
                          const name = (option.name || option.user?.name || '').toLowerCase();
                          const phone = (option.phoneNumber || option.phone || option.user?.phoneNumber || '').toLowerCase();
                          const email = (option.email || option.user?.email || '').toLowerCase();
                          return name.includes(term) || phone.includes(term) || email.includes(term);
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="App-Kunde verknüpfen"
                          helperText="Optional: Verknüpft die Verordnung mit einem registrierten Kundenkonto für spätere App-Bestellungen."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <PersonIcon color="primary" sx={{ mr: 1 }} />
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Patient" value={form.patientName} onChange={handleChange('patientName')} error={!!formErrors.patientName} helperText={formErrors.patientName} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Telefon" value={form.patientPhone} onChange={handleChange('patientPhone')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Krankenkasse" value={form.healthInsuranceName} onChange={handleChange('healthInsuranceName')} error={!!formErrors.healthInsuranceName} helperText={formErrors.healthInsuranceName} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Versichertennummer" value={form.insuranceNumber} onChange={handleChange('insuranceNumber')} fullWidth />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Genehmigung</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Verordnungsnummer" value={form.prescriptionNumber} onChange={handleChange('prescriptionNumber')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Genehmigungsnummer" value={form.approvalNumber} onChange={handleChange('approvalNumber')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label="Genehmigungsstatus" value={form.approvalStatus} onChange={handleChange('approvalStatus')} fullWidth>
                      {optionItems(APPROVAL_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Genehmigt bis" type="date" value={form.approvalValidUntil} onChange={handleChange('approvalValidUntil')} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={<Checkbox checked={form.approvalRequired} onChange={handleChange('approvalRequired')} />}
                      label="Genehmigung erforderlich"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Zeitraum und Kontingent</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Gültig ab" type="date" value={form.validFrom} onChange={handleChange('validFrom')} error={!!formErrors.validFrom} helperText={formErrors.validFrom} slotProps={{ inputLabel: { shrink: true } }} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Gültig bis" type="date" value={form.validUntil} onChange={handleChange('validUntil')} error={!!formErrors.validUntil} helperText={formErrors.validUntil} slotProps={{ inputLabel: { shrink: true } }} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label="Max. Fahrten" type="number" value={form.maxRides} onChange={handleChange('maxRides')} error={!!formErrors.maxRides} helperText={formErrors.maxRides} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Geplante Fahrten" type="number" value={form.plannedRides} onChange={handleChange('plannedRides')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Genutzte Fahrten" type="number" value={form.usedRides} onChange={handleChange('usedRides')} fullWidth />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Strecke und Behandlung</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label="Streckenbindung" value={form.routeScope} onChange={handleChange('routeScope')} fullWidth>
                      {optionItems(ROUTE_SCOPE_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label="Behandlungstyp" value={form.treatmentType} onChange={handleChange('treatmentType')} fullWidth>
                      {optionItems(TREATMENT_TYPE_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <PlaceAutocomplete
                      label="Startadresse"
                      value={form.pickupAddress}
                      onChange={(event) => setForm(prev => ({ ...prev, pickupAddress: event.target.value }))}
                      onPlaceSelect={handlePickupSelect}
                      error={!!formErrors.pickupAddress}
                      helperText={formErrors.pickupAddress}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={12}>
                    <PlaceAutocomplete
                      label="Ziel / Einrichtung"
                      value={form.destinationAddress}
                      onChange={(event) => setForm(prev => ({ ...prev, destinationAddress: event.target.value }))}
                      onPlaceSelect={handleDestinationSelect}
                      error={!!formErrors.destinationAddress}
                      helperText={formErrors.destinationAddress}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Sonderbedarf</Typography>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField select label="Bedarfsart" value={form.transportRequirementMode} onChange={handleTransportRequirementModeChange} fullWidth>
                      <MenuItem value="vehicle_type">Fahrzeugtyp festlegen</MenuItem>
                      <MenuItem value="capability">Spezialfähigkeit festlegen</MenuItem>
                    </TextField>
                  </Grid>
                  {form.transportRequirementMode === 'vehicle_type' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField select label="Standard-Fahrzeugtyp" value={form.defaultVehicleType} onChange={handleChange('defaultVehicleType')} error={!!formErrors.defaultVehicleType} helperText={formErrors.defaultVehicleType} fullWidth>
                        {VEHICLE_TYPE_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  {form.transportRequirementMode === 'capability' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel id="required-capabilities-label">Spezialfähigkeiten</InputLabel>
                        <Select
                          labelId="required-capabilities-label"
                          multiple
                          value={form.requiredCapabilities}
                          onChange={handleChange('requiredCapabilities')}
                          input={<OutlinedInput label="Spezialfähigkeiten" />}
                          renderValue={(selected) => selected.map(value => SPECIAL_CAPABILITIES[value] || value).join(', ')}
                          error={!!formErrors.requiredCapabilities}
                        >
                          {capabilityOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={form.requiredCapabilities.includes(option.value)} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.requiredCapabilities && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {formErrors.requiredCapabilities}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  <Grid size={12}>
                    <FormControlLabel
                      control={<Checkbox checked={form.escortRequired} onChange={handleChange('escortRequired')} />}
                      label="Begleitperson erforderlich"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Nachweis und Abrechnung</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField select label="Zuzahlung" value={form.copaymentStatus} onChange={handleChange('copaymentStatus')} fullWidth>
                      {optionItems(COPAYMENT_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField select label="Dokumente" value={form.documentStatus} onChange={handleChange('documentStatus')} fullWidth>
                      {optionItems(DOCUMENT_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField select label="Abrechnung" value={form.billingStatus} onChange={handleChange('billingStatus')} fullWidth>
                      {optionItems(BILLING_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="Interne Notizen / Fahrerhinweise"
                      value={form.defaultNotes}
                      onChange={handleChange('defaultNotes')}
                      fullWidth
                      multiline
                      minRows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ mt: 3, p: 2 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => navigate('/krankenfahrten')}>
              Abbrechen
            </Button>
            <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={isSaving}>
              {isSaving ? 'Speichert...' : 'Speichern'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
    </APIProvider>
  );
};

export default MedicalTransportOrderForm;
